import React from "react"
import axios from "axios"
import { styled } from "@mui/material/styles"
import {
  Button,
  Drawer,
  Grid,
  IconButton,
  Link,
  List,
  TextField,
} from "@mui/material"
import { Provider, observer } from "mobx-react"
import { logEvent } from "../utils/analytics"
import CommentCard from "./CommentCard"
import SFSnackbar from "./SFSnackbar"
import ThemeProviderWrapper from "./ThemeProviderWrapper"
import snackbarStore from '../stores/snackbarStore';
import { handleShiftEnterSubmit } from "../utils/inputUtils"

const Overlay = styled("div")`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: transparent;
  z-index: 1000;
`

const DrawerContainer = styled("div")`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 400px;
  border: 0.5px solid #bababa;
`

const CommentsListContainer = styled("div")`
  overflow-y: auto;
  flex-grow: 1;
`

const HeaderGrid = styled(Grid)`
  padding: 0px;
  display: flex;
  align-items: center;
`

const CloseIcon = styled("div")`
  cursor: pointer;
  font-weight: 300;
  font-size: 25px;
  color: #757575;
  text-align: center;
`

const SFIconButton = styled(IconButton)`
  border-radius: 50%;
  width: 40px;
  height: 40px;
  padding: 8px;
`

const Title = styled("p")`
  font-weight: 800;
  font-size: 18px;
  text-align: left;
  flex-grow: 1;
  color: #3f3f3f;
`

const ToggleButtonContent = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-transform: uppercase;
  margin-right: 10px;
`

const ToggleButton = styled(Button)`
  color: #626161;
  font-size: 16px;
  border: 1px solid #626161;
  width: 100%;
  height: 54px;
  letter-spacing: .5px;

  i {
    font-size: 1.6rem;
  }
`

const LogInNotice = styled("div")`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  padding: 20vh 0;
  border-top: 0.5px solid #333333;
`

const TextareaContainer = styled("div")`
  position: sticky;
  bottom: 0;
  padding: 8px;
`

const CommentTextfield = styled(TextField)`
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 8px 5px;
`

const ButtonsContainer = styled("div")`
  display: flex;
  justify-content: space-evenly;
  margin: 0;
  font-size: 18px;
  padding: 3px 0px 8px;
`

const ActionButton = styled(Button)`
  min-width: 48%;
  margin: 0px;
  text-decoration: none;
  &:hover {
    text-decoration: none;
  }
`
const stores = {
  snackbarStore,
}

@observer
export default class CommentWidget extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      isDrawerOpen: false,
      comments: props.comments || [],
      commentText: "",
      highlightedCommentId: null,
      snackbarStatus: {
        open: false,
        message: "",
        severity: "info",
      },
      drawerOpenOnMount: false
    }
    this.commentsListRef = React.createRef()
  }

  componentDidMount() {
    const params = new URLSearchParams(window.location.search)
    const commentId = params.get("commentId")
    const { comments } = this.state
    const { openCommentsDrawerOnMount } = this.props

    if (commentId) {
      if (comments.some(comment => comment.id.toString() === commentId)) {
        this.setState({ highlightedCommentId: commentId, isDrawerOpen: true })
        logEvent("Direct link to specific comment.")
        this.scrollToComment(commentId)
      } else {
        this.setState({ isDrawerOpen: false })
        snackbarStore.showSnackbar("The comment you're looking for doesn't exist or has been deleted.", "info")
      }
    }

    if (window.innerWidth > 1880 && openCommentsDrawerOnMount) {
      this.setState({ isDrawerOpen: true, drawerOpenOnMount: true })
    }
  }

  scrollToComment = (commentId) => {
    const element = document.getElementById(`comment-${commentId}`)
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" })
    }
  }

  toggleDrawer = () => {
    this.setState((prevState) => ({ isDrawerOpen: !prevState.isDrawerOpen }))
  }

  handleTextChange = (event) => {
    this.setState({ commentText: event.target.value })
  }

  handleCancel = () => {
    this.setState({ commentText: "" })
  }

  handleKeyDown = (event) => {
    handleShiftEnterSubmit(
      event,
      this.state.commentText,
      this.handleSubmitComment
    )
  }

  handleSubmitComment = async (body, parentId = null) => {
    const { commentableType, commentable } = this.props
    axios.defaults.headers.common["Accept"] = "application/json"

    try {
      const response = await axios({
        method: "POST",
        url: `/comments`,
        data: {
          commentable_type: commentableType,
          commentable_id: commentable.id,
          body: body,
          parent_id: parentId,
        },
      })
      const newComment = response.data.comment
      this.setState(
        (prevState) => {
          if (parentId) {
            // If it's a reply, find the parent comment and add the reply to it
            return {
              comments: prevState.comments.map((comment) =>
                comment.id === parentId
                  ? {
                      ...comment,
                      replies: [...(comment.replies || []), newComment],
                    }
                  : comment
              ),
            }
          } else {
            // If it's a top-level comment, just add it to the comments list
            return {
              comments: [...prevState.comments, newComment],
            }
          }
        },
        () => {
          this.scrollToComment(newComment.id)
        }
      )
      this.setState({ commentText: "" })
      logEvent("Submitted new comment.")
    } catch (error) {
      console.error("Error submitting comment:", error)
      snackbarStore.showSnackbar("Failed to submit comment. Please try again.", "error")
    }
  }

  handleDeleteComment = async (commentId, parentId = null) => {
    axios.defaults.headers.common["Accept"] = "application/json"
    try {
      await axios.delete(`/comments/${commentId}`)
      this.setState((prevState) => {
        if (parentId) {
          // If it's a reply, find the parent comment and remove the reply
          return {
            comments: prevState.comments.map((comment) =>
              comment.id === parentId
                ? {
                    ...comment,
                    replies: comment.replies.filter(
                      (reply) => reply.id !== commentId
                    ),
                  }
                : comment
            ),
          }
        } else {
          // If it's a top-level comment, just remove it from the comments list
          return {
            comments: prevState.comments.filter(
              (comment) => comment.id !== commentId
            ),
          }
        }
      })
    } catch (error) {
      console.error("Error deleting comment:", error)
      snackbarStore.showSnackbar("Failed to delete comment. Please try again.", "error")
    }
  }

  renderTextArea() {
    const { commentText } = this.state
    const { currentUser } = this.props
    const isButtonDisabled = Boolean(!commentText)

    return (
      <TextareaContainer>
        {currentUser ? (
          <div>
            <CommentTextfield
              id="outlined-multiline-static"
              multiline
              label="Leave a comment"
              rows={5}
              value={commentText}
              fullWidth
              onChange={this.handleTextChange}
              onKeyDown={this.handleKeyDown}
              aria-label="Comment input (cannot be empty)"
            />
            <ButtonsContainer>
              <ActionButton
                variant="text"
                disabled={isButtonDisabled}
                onClick={this.handleCancel}
                aria-label="Cancel comment"
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant="contained"
                color="primary"
                disabled={isButtonDisabled}
                onClick={() => this.handleSubmitComment(commentText)}
                aria-label="Submit comment"
              >
                Comment
              </ActionButton>
            </ButtonsContainer>
          </div>
        ) : (
          <LogInNotice>
            <strong>
              You must <Link href="../users/sign_in" aria-label="You must sign in to comment">sign-in</Link> to comment.
            </strong>
          </LogInNotice>
        )}
      </TextareaContainer>
    )
  }

  renderComments() {
    const { comments, highlightedCommentId } = this.state
    const { currentUser, currentUserAvatar } = this.props

    return comments
      .filter((comment) => comment.parent_id === null)
      .map((comment) => (
        <div key={comment.id} id={`comment-${comment.id}`}>
          <CommentCard
            comment={comment}
            replies={comment.replies}
            comments={comments}
            currentUser={currentUser}
            currentUserAvatar={currentUserAvatar}
            handleDeleteComment={this.handleDeleteComment}
            handleSubmitComment={this.handleSubmitComment}
            commentsListRef={this.commentsListRef}
            highlightedCommentId={highlightedCommentId}
            isHighlightedComment={
              highlightedCommentId === comment.id.toString()
            }
          />
        </div>
      ))
  }

  render() {
    const { comments, isDrawerOpen, drawerOpenOnMount } = this.state
    const commentsCount = comments.length

    return (
      <Provider {...stores}>
        <ThemeProviderWrapper>
          {isDrawerOpen && !drawerOpenOnMount && <Overlay onClick={this.toggleDrawer}></Overlay>}
          <ToggleButton
            onClick={this.toggleDrawer}
            variant="outlined"
            aria-label={`Toggle comments drawer with ${commentsCount} comments`}
          >
            <ToggleButtonContent>
              <i className="fa fa-comments fa-regular left" aria-hidden="true"></i>
              {commentsCount} comments
            </ToggleButtonContent>
          </ToggleButton>
          <Drawer anchor="right" variant="persistent" open={isDrawerOpen}>
            <DrawerContainer>
              <HeaderGrid container>
                <Grid item xs={2}>
                  <SFIconButton onClick={this.toggleDrawer} aria-label="Close comments drawer">
                    <CloseIcon>
                      <i className="fa fa-angle-right" aria-hidden="true"></i>
                    </CloseIcon>
                  </SFIconButton>
                </Grid>
                <Grid item xs={10}>
                  <Title>{commentsCount} Comments</Title>
                </Grid>
              </HeaderGrid>
              <CommentsListContainer ref={this.commentsListRef}>
                <List sx={{ padding: "0" }}>{this.renderComments()}</List>
              </CommentsListContainer>
              {this.renderTextArea()}
            </DrawerContainer>
          </Drawer>
          <SFSnackbar/>
        </ThemeProviderWrapper>
      </Provider>
    )
  }
}
